import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import image from "../../Images/image 306.svg";
import {
  Clock,
  BookOpen,
  GraduationCap,
  Languages,
  Star,
  MapPin,
} from "lucide-react";
import { API_URL } from "../../config/api";
import ToastMessage from "../../config/ToastMessage";

export default function CourseDetailPage() {
  const { course_id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  //  Selected schedule from navigation OR URL
  const queryParams = new URLSearchParams(location.search);
  const selectedScheduleIdFromUrl = queryParams.get("schedule");

  const selectedScheduleId =
    location.state?.scheduleId ||
    (selectedScheduleIdFromUrl ? Number(selectedScheduleIdFromUrl) : null);

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeWeek, setActiveWeek] = useState(0);
  const [toast, setToast] = useState({ type: "", message: "" });

  // ------------------ HELPERS ------------------

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
  };

  // ✅ Duration calculation includes both start and end dates
  const getProgramDuration = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";

    const start = new Date(startDate);
    const end = new Date(endDate);

    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;

    if (diffDays <= 30) {
      return `${diffDays} days`;
    } else {
      const diffMonths = Math.ceil(diffDays / 30);
      return `${diffMonths} month${diffMonths > 1 ? "s" : ""}`;
    }
  };

  //  Get selected schedule instead of always first
  const getSelectedSchedule = (courseData) => {
    if (!courseData?.schedules?.length) return null;

    if (selectedScheduleId) {
      const found = courseData.schedules.find(
        (s) => s.schedule_id === selectedScheduleId,
      );
      if (found) return found;
    }

    // Fallback
    return courseData.schedules[0];
  };

  // 🔹 Check if discount is currently valid
  const isDiscountValid = (schedule) => {
    if (
      !schedule ||
      !schedule.discount_applicable ||
      !schedule.discount_value
    ) {
      return false;
    }

    if (!schedule.discount_valid_from && !schedule.discount_valid_to) {
      return true;
    }

    const now = new Date();
    const validFrom = schedule.discount_valid_from
      ? new Date(schedule.discount_valid_from)
      : null;
    const validTo = schedule.discount_valid_to
      ? new Date(schedule.discount_valid_to)
      : null;

    if (validFrom && now < validFrom) return false;
    if (validTo && now > validTo) return false;

    return true;
  };

  // 🔹 Calculate discounted price
  const calculateDiscountedPrice = (schedule, originalPrice) => {
    if (!isDiscountValid(schedule)) {
      return originalPrice;
    }

    const discountType = schedule.discount_type;
    const discountValue = Number(schedule.discount_value);
    const price = Number(originalPrice);

    if (discountType === "Percentage") {
      const discountAmount = (price * discountValue) / 100;
      return price - discountAmount;
    } else if (discountType === "FlatAmount") {
      return Math.max(0, price - discountValue);
    }

    return originalPrice;
  };

  // ------------------ API ------------------

  useEffect(() => {
    axios
      .get(`${API_URL}/courses/${course_id}/`)
      .then((res) => setCourse(res.data.data || res.data))
      .catch((err) => console.error("Error fetching course:", err))
      .finally(() => setLoading(false));
  }, [course_id]);

  // ------------------ HANDLERS ------------------

  const handleSubmit = () => {
    const user = localStorage.getItem("access_token");

    if (!user) {
      setToast({
        type: "error",
        message: "Please log in to continue to payment.",
      });
      setTimeout(() => navigate("/login"), 1800);
    } else {
      const schedule = getSelectedSchedule(course);
      const originalPrice = course.amount ? Number(course.amount) : 0;
      const finalPrice = schedule
        ? calculateDiscountedPrice(schedule, originalPrice)
        : originalPrice;

      setToast({ type: "success", message: "Redirecting to payment page..." });
      setTimeout(() => {
        navigate("/payment", {
          state: {
            schedule_id: schedule?.schedule_id || null,
            course_id: course.course_id,
            course_name: course.title,
            duration: schedule
              ? getProgramDuration(schedule.start_date, schedule.end_date)
              : "N/A",
            lectures: course.total_lessons,
            skill_level: course.level,
            language: course.language,
            amount: finalPrice,
            original_amount: originalPrice,
            start_date: schedule?.start_date,
            end_date: schedule?.end_date,
            location: schedule?.location || "N/A",
          },
        });
      }, 1200);
    }
  };

  // ------------------ STATES ------------------

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen text-gray-600">
        Loading course details...
      </div>
    );

  if (!course)
    return (
      <div className="flex justify-center items-center min-h-screen text-red-500">
        Course not found.
      </div>
    );

  const instructor =
    course.instructors && course.instructors.length > 0
      ? course.instructors[0].user
      : null;

  //  Use selected schedule everywhere
  const schedule = getSelectedSchedule(course);
  const originalPrice = course.amount ? Number(course.amount) : 0;
  const discountedPrice = schedule
    ? calculateDiscountedPrice(schedule, originalPrice)
    : originalPrice;

  const hasDiscount =
    schedule && isDiscountValid(schedule) && discountedPrice < originalPrice;

  const details = [
    {
      icon: <Clock size={20} />,
      label: "Duration:",
      value: schedule
        ? getProgramDuration(schedule.start_date, schedule.end_date)
        : "N/A",
    },
    {
      icon: <BookOpen size={20} />,
      label: "Lessons:",
      value: course.total_lessons || "N/A",
    },
    {
      icon: <GraduationCap size={20} />,
      label: "Skill Level:",
      value: course.level || "N/A",
    },
    {
      icon: <Languages size={20} />,
      label: "Language:",
      value: course.language === "en" ? "English" : course.language || "N/A",
    },
    {
      icon: <MapPin size={20} />,
      label: "Location:",
      value: schedule?.location || "N/A",
    },
  ];

  // ------------------ UI ------------------

  return (
    <div className="bg-white min-h-screen">
      {/* Toast Message */}
      {toast.message && (
        <ToastMessage
          type={toast.type}
          message={toast.message}
          onClose={() => setToast({ type: "", message: "" })}
        />
      )}

      {/* ===== Hero Section ===== */}
      <div className="relative w-full h-[400px] mb-10">
        <img
          src={course.program_banner}
          alt="Course Banner"
          className="w-full h-full object-cover"
        />
        <div className="absolute w-1/2 inset-0 bg-gradient-to-r from-white via-white/70 to-transparent"></div>
        <div className="absolute inset-0 flex items-center pl-16">
          <h1 className="text-zinc-800 text-4xl font-bold font-['Roboto']">
            Programs Details
          </h1>
        </div>
      </div>

      {/* ===== Main Layout ===== */}
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-8 px-6">
        {/* ===== Left Sidebar ===== */}
        <aside className="flex flex-col gap-8 w-full lg:w-1/4">
          {/* Course Info */}
          <div className="border-2 border-dashed rounded-md p-4 space-y-4">
            {details.map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 border-b pb-2 last:border-b-0"
              >
                <div className="bg-[#4AACC5] text-white p-2 rounded-full flex items-center justify-center">
                  {item.icon}
                </div>
                <div>
                  <span className="text-gray-500 text-sm font-medium">
                    {item.label}
                  </span>{" "}
                  <span className="text-gray-800 text-sm font-medium">
                    {item.value}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Recommended Courses */}
          <div className="border-2 rounded-lg p-5">
            <h2 className="text-zinc-800 text-2xl font-semibold mb-6 font-['Roboto']">
              Recommended Courses
            </h2>
            <div className="flex flex-col gap-6">
              {[
                "Critical Care Nursing",
                "Pediatric Nursing",
                "ECG & Cardiac Care",
              ].map((title, index) => (
                <div
                  key={index}
                  className="flex items-center gap-4 pb-4 border-b last:border-b-0"
                >
                  <img
                    src="https://placehold.co/96x93"
                    alt={title}
                    className="w-20 h-20 rounded-md border border-zinc-300 shadow-sm object-cover"
                  />
                  <div>
                    <h3 className="text-lg text-zinc-800 font-medium capitalize">
                      {title}
                    </h3>
                    <p className="text-sm text-neutral-500">
                      by{" "}
                      <span className="text-zinc-800 font-medium capitalize">
                        Advanced
                      </span>
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          size={14}
                          className={`${
                            i < 4
                              ? "fill-amber-400 text-amber-400"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                      <span className="ml-1 text-xs text-gray-600">(4)</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* ===== Right Content ===== */}
        <section className="flex-1 space-y-8">
          {/* Course Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center border-b pb-6 mb-6">
            <div>
              <p className="text-[#4AACC5] text-sm font-semibold uppercase tracking-wide items-start">
                Program Start Date:
                <span className="text-gray-700 ml-1">
                  {schedule ? formatDate(schedule.start_date) : "N/A"}
                </span>
              </p>

              {/* <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                <MapPin size={14} className="text-[#4AACC5]" />
                {schedule?.location || "N/A"}
              </p> */}

              <h2 className="text-2xl font-bold text-gray-900 mt-1">
                {course.title || "Course Title"}
              </h2>

              <div className="flex items-center flex-wrap gap-4 mt-4">
                <div className="flex items-center gap-3">
                  <img
                    src={instructor?.avatar_url || "https://placehold.co/55x55"}
                    alt="Instructor"
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-gray-800 font-medium">
                      {instructor?.name}
                    </h3>
                    <div className="flex items-center text-gray-500 text-sm">
                      <BookOpen size={15} className="mr-1 text-[#4AACC5]" />
                      <span> {course.total_modules} Modules</span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center text-gray-600 text-sm">
                  <Clock size={15} className="mr-1 text-[#4AACC5]" />
                  <span>
                    <strong>
                      {schedule
                        ? `${formatDate(schedule.start_date)} to ${formatDate(
                            schedule.end_date,
                          )}`
                        : "N/A"}
                    </strong>
                  </span>
                </div>
              </div>

              <div className="flex items-center gap-1 mt-3">
                {Array.from({ length: 5 }).map((_, i) => {
                  const rating = course.average_rating || 0;
                  const isFullStar = i < Math.floor(rating);
                  const isHalfStar =
                    i === Math.floor(rating) && rating % 1 >= 0.5;

                  if (isHalfStar) {
                    return (
                      <div
                        key={i}
                        className="relative inline-block"
                        style={{ width: "16px", height: "16px" }}
                      >
                        <Star size={16} className="absolute text-gray-300" />
                        <div
                          className="absolute overflow-hidden"
                          style={{ width: "50%" }}
                        >
                          <Star
                            size={16}
                            className="fill-amber-400 text-amber-400"
                          />
                        </div>
                      </div>
                    );
                  }

                  return (
                    <Star
                      key={i}
                      size={16}
                      className={`${
                        isFullStar
                          ? "fill-amber-400 text-amber-400"
                          : "text-gray-300"
                      }`}
                    />
                  );
                })}
                <span className="text-gray-700 text-sm ml-1">
                  ({course.average_rating?.toFixed(1) || "No Rating"})
                </span>
              </div>
            </div>

            <div className="mt-5 md:mt-0 text-right">
              <p className="text-sm font-semibold text-gray-500 uppercase">
                Program Price
              </p>

              {hasDiscount ? (
                <div className="mt-1">
                  <div className="flex items-center justify-end gap-2">
                    <span className="text-lg text-red-500 line-through">
                      ${originalPrice.toFixed(2)}
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      ${discountedPrice.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    Save ${(originalPrice - discountedPrice).toFixed(2)}!
                  </p>
                  {schedule?.discount_valid_to && (
                    <p className="text-xs text-red-500 mt-1">
                      Limited Offer Valid Till:{" "}
                      {formatDate(schedule.discount_valid_to)}
                    </p>
                  )}
                </div>
              ) : (
                <h3 className="text-2xl font-bold text-gray-900 mt-1">
                  ${originalPrice.toFixed(2)}
                </h3>
              )}

              <button
                className="mt-3 bg-[#4AACC5] hover:bg-teal-600 text-white font-medium px-5 py-2 rounded-lg"
                onClick={handleSubmit}
              >
                Buy this Program
              </button>
            </div>
          </div>

          {/* Course Overview */}
          <div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-3">
              Program Overview
            </h3>
            <p className="text-gray-600 leading-relaxed">
              {course.description || "No description provided."}
            </p>
          </div>

          {/* === Modules Section === */}
          <div>
            <h3 className="text-2xl font-semibold text-gray-800 mb-3">
              Advance Your Career With {course.title}
            </h3>

            <div className="flex flex-wrap gap-3 mb-8 items-center">
              {course.modules?.length > 0 ? (
                course.modules.map((mod, i) => (
                  <button
                    key={mod.module_id || i}
                    onClick={() => setActiveWeek(i)}
                    className={`px-5 py-2 text-sm font-medium rounded-full border transition-all duration-200 ${
                      activeWeek === i
                        ? "bg-[#20c0d7] text-white border-[#20c0d7]"
                        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                    }`}
                  >
                    Module {i + 1}
                  </button>
                ))
              ) : (
                <p className="text-gray-500">No modules found.</p>
              )}
            </div>

            {course.modules?.[activeWeek] ? (
              <div className="flex flex-col border rounded-lg p-5 bg-gray-50">
                <h4 className="flex items-start font-semibold text-gray-800 mb-3">
                  {course.modules[activeWeek].title}
                </h4>

                {course.modules[activeWeek].lessons?.length > 0 ? (
                  course.modules[activeWeek].lessons.map((lesson) => (
                    <div
                      key={lesson.lesson_id}
                      className="pl-3 border-l-4 border-teal-400 mb-4"
                    >
                      <p className="flex item] font-medium text-gray-800">
                        {lesson.title}
                      </p>

                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        <span className="text-xs text-gray-500 font-medium">
                          Type:
                        </span>

                        {lesson.assessment_list?.length > 0 ? (
                          lesson.assessment_list.map((type, idx) => (
                            <span
                              key={idx}
                              className="text-xs bg-sky-100 text-sky-700 px-2 py-0.5 rounded-full"
                            >
                              {type}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-500">
                    No lessons available for this module.
                  </p>
                )}
              </div>
            ) : (
              <p className="text-gray-600">
                Select a topic to view module details.
              </p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
